import {Calendar, CalendarDateTemplateEvent} from "primereact/calendar";
import {ListBox} from "primereact/listbox";
import {AvailableSlot} from "../data/Slot";
import {Nullable} from "primereact/ts-helpers";
import {format, parse} from "date-fns";
import {Panel} from "primereact/panel";

function CalendarComponent({
                               selectedDate,
                               availableSlots,
                               onDateSelected,
                           }: {
    selectedDate: Date | null;
    availableSlots: AvailableSlot[];
    onDateSelected(date: Nullable<Date>): void;
}) {
    const zeroPad = (num: number, places: number) =>
        String(num).padStart(places, "0");

    const availableDates = Array.from(
        new Set(
            availableSlots.map(
                (slot) => format(slot.startDateTime, "yyyy-MM-dd"),
            ),
        ),
    ).map(dateString => parse(dateString, "yyyy-MM-dd", new Date())).sort((a, b) => a.getTime() - b.getTime());

    const formattedDates = availableDates.map((date) => ({
        label: format(date, "dd.MM.yyyy"),
        value: date,
    }));

    const handleCalendarChange = (event: { value: Nullable<Date> }) => {
        const selectedDate = event.value;
        if (selectedDate) {
            const formattedSelectedDate = format(selectedDate, "yyyy-MM-dd");
            const isDateAvailable = availableSlots
                .map((slot) => format(slot.startDateTime, "yyyy-MM-dd"))
                .includes(formattedSelectedDate);

            if (isDateAvailable) {
                const correspondingDate = availableDates.find(
                    (date) => format(date, "yyyy-MM-dd") === formattedSelectedDate,
                );
                onDateSelected(correspondingDate || selectedDate);
            } else {
                onDateSelected(selectedDate);
            }
        } else {
            onDateSelected(null);
        }
    };

    return (
        <Panel header="Verfügbare Termine" className=" md:h-full">
            <div className="flex flex-column md:flex-row gap-4">
                <div className="flex-1 md:w-4">
                    <ListBox
                        options={formattedDates}
                        value={selectedDate}
                        onChange={(event) => onDateSelected(event.value)}
                        optionLabel="label"
                        className="w-full"
                        style={{maxHeight: "300px", overflow: "auto"}}
                    />
                </div>
                <div className="flex-3 md:w-8">
                    <Calendar
                        id="slotCalendar"
                        value={selectedDate}
                        onChange={handleCalendarChange}
                        dateTemplate={(event: CalendarDateTemplateEvent) => {
                            const slots = availableSlots.filter((slot) => format(slot.startDateTime, "yyyy-MM-dd") == `${zeroPad(event.year, 4)}-${zeroPad(event.month + 1, 2)}-${zeroPad(event.day, 2)}`);

                            if (slots.length <= 0) {
                                return <p className="text-color-secondary">{event.day}</p>;
                            }
                            const isFull = slots.every((slot) => slot.available == 0)
                            const isDisabled = slots.filter((slot) => slot.available > 0).every((slot) => slot.disabled)

                            if (isFull) {
                                return (
                                    <div
                                        className="flex border bg-red-100 border-red-500 border-circle w-full h-full align-items-center justify-content-center font-bold"
                                        style={{
                                            border: "0.2rem solid",
                                        }}
                                    >
                                        {event.day}
                                    </div>
                                );
                            } else if (isDisabled) {
                                return (
                                    <div
                                        className="flex border bg-gray-100 border-gray-500 border-circle w-full h-full align-items-center justify-content-center font-bold"
                                        style={{
                                            border: "0.2rem solid",
                                        }}
                                    >
                                        {event.day}
                                    </div>
                                );
                            }
                            return (
                                <div
                                    className="flex border bg-green-100 border-green-500 border-circle w-full h-full align-items-center justify-content-center font-bold"
                                    style={{
                                        border: "0.2rem solid",
                                    }}
                                >
                                    {event.day}
                                </div>
                            );
                        }}
                        inline
                        className="w-full border-round-xl"
                    />
                </div>
            </div>
        </Panel>
    );
}

export default CalendarComponent;
